import React, { useState, useEffect } from 'react';
import { vehicleService, studentService } from '../../services/index.js';
import subUserService from '../../services/SubUserService.js';
import { 
  Bus, MapPin, Users, Wrench, Plus, Edit, Trash2, Search, Filter, RefreshCw, CheckCircle, AlertTriangle, Printer, ArrowRight, Clock, ShieldCheck, Phone, Calendar
} from 'lucide-react';

const VehicleManagement = ({ orgData, students: propStudents = [], showNotification }) => {
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'vehicles', 'routes', 'assignments', 'reports'

  // Core Data States
  const [vehicles, setVehicles] = useState([]);
  const [vehicleRoutes, setVehicleRoutes] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState([]);
  const [allStudents, setAllStudents] = useState(propStudents || []);
  const [, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (Array.isArray(propStudents) && propStudents.length > 0) {
      setAllStudents(propStudents);
    }
  }, [propStudents]);

  // Selected Item / Filter States
  const [, setSelectedRouteForStops] = useState(null);
  const [, setRouteStopsMap] = useState({});

  // Modals
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [editingVehicleId, setEditingVehicleId] = useState(null);

  const [showRouteModal, setShowRouteModal] = useState(false);
  const [, setEditingRouteName] = useState(null);

  const [, setShowStopModal] = useState(false);
  const [, setStopRouteId] = useState('');

  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);

  // Filters for Student Assignments
  const [filterClass, setFilterClass] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [modalStudentSearch, setModalStudentSearch] = useState('');

  // Form States
  const [vehicleForm, setVehicleForm] = useState({
    registrationNumber: '',
    vehicleType: 'BUS',
    vehicleModel: '',
    capacity: 40,
    driverId: '',
    driverName: '',
    driverPhone: '',
    driverPassword: '',
    route: '',
    alertDistanceKm: 3.0,
    status: 'ACTIVE'
  });

  const [routeForm, setRouteForm] = useState({
    routeName: '',
    startPoint: '',
    endPoint: '',
    distance: '',
    estimatedTime: '',
    vehicleId: ''
  });

  const [, setStopForm] = useState({
    stopName: '',
    sequenceNo: 1,
    pickupTime: '07:30 AM',
    dropTime: '04:30 PM',
    latitude: 15.0,
    longitude: 76.0
  });

  const [assignmentForm, setAssignmentForm] = useState({
    studentId: '',
    vehicleId: '',
    routeName: '',
    pickupPoint: '',
    dropPoint: '',
    alertDistanceKm: 3.0,
    startDate: new Date().toISOString().split('T')[0]
  });

  // Load all data on mount
  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadVehicles(),
        loadAssignments(),
        loadStudents(),
        loadDrivers()
      ]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const res = await vehicleService.listVehicles();
      const list = Array.isArray(res) ? res : (res?.data || res?.vehicles || []);
      setVehicles(list);

      // Extract unique routes from vehicles
      const routesList = list
        .filter(v => v.route && v.route.trim() !== '')
        .map(v => {
          const points = v.pickupPoints ? v.pickupPoints.split(',').map(p => p.trim()) : [];
          return {
            routeName: v.route,
            startPoint: points[0] || 'School Depot',
            endPoint: points[points.length - 1] || 'Campus Central',
            distance: v.routeDistance !== undefined && v.routeDistance !== null ? v.routeDistance : '12',
            estimatedTime: v.routeTime || '45 mins',
            vehicleId: v.id,
            vehicleReg: v.registrationNumber,
            driverName: v.driverName,
            driverPhone: v.driverPhone,
            capacity: v.capacity || 40
          };
        });
      setVehicleRoutes(routesList);
    } catch (err) {
      console.error('Error loading vehicles:', err);
    }
  };

  const loadAssignments = async () => {
    try {
      const res = await vehicleService.listAllocations();
      const list = Array.isArray(res) ? res : (res?.data || []);
      setVehicleAssignments(list);
    } catch (err) {
      console.error('Error loading allocations:', err);
    }
  };

  const loadStudents = async () => {
    try {
      if (Array.isArray(propStudents) && propStudents.length > 0) {
        setAllStudents(propStudents);
        return;
      }
      const res = await studentService.getStudents();
      const list = Array.isArray(res) ? res : (res?.data || res?.students || []);
      setAllStudents(list);
    } catch (err) {
      console.error('Error loading students:', err);
    }
  };

  const loadDrivers = async () => {
    try {
      const res = await subUserService.getAllSubUsers();
      if (res && res.success && Array.isArray(res.users)) {
        setDrivers(res.users.filter(u => u.role === 'DRIVER'));
      }
    } catch (err) {
      console.error('Error loading drivers:', err);
    }
  };

  // Helper for safe date display
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'Invalid Date') return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateStr;
    }
  };

  // Handlers for Vehicle
  const handleSaveVehicle = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        registrationNumber: vehicleForm.registrationNumber,
        vehicleType: vehicleForm.vehicleType,
        vehicleModel: vehicleForm.vehicleModel,
        capacity: parseInt(vehicleForm.capacity) || 40,
        driverName: vehicleForm.driverName,
        driverPhone: vehicleForm.driverPhone,
        driverPassword: vehicleForm.driverPassword,
        route: vehicleForm.route,
        alertDistanceKm: parseFloat(vehicleForm.alertDistanceKm) || 3.0,
        status: vehicleForm.status
      };

      if (editingVehicleId) {
        await vehicleService.updateVehicle(editingVehicleId, payload);
        showNotification('success', 'Success', 'Vehicle updated successfully');
      } else {
        await vehicleService.createVehicle(payload);
        showNotification('success', 'Success', 'Vehicle added successfully');
      }

      setShowVehicleModal(false);
      setEditingVehicleId(null);
      loadVehicles();
    } catch (err) {
      console.error(err);
      showNotification('error', 'Error', err.message || 'Failed to save vehicle');
    } finally {
      setLoading(false);
    }
  };

  // Handlers for Allocation
  const handleSaveAssignment = async (e) => {
    e.preventDefault();
    if (!assignmentForm.studentId || !assignmentForm.vehicleId) {
      showNotification('warning', 'Warning', 'Please select student and vehicle');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...assignmentForm,
        alertDistanceKm: parseFloat(assignmentForm.alertDistanceKm) || 3.0
      };

      if (editingAssignmentId) {
        const res = await vehicleService.updateAllocation(editingAssignmentId, payload);
        if (res && res.success) {
          showNotification('success', 'Success', 'Student transport settings & alert distance updated');
          setShowAssignmentModal(false);
          setEditingAssignmentId(null);
          loadAssignments();
        } else {
          showNotification('error', 'Error', res?.error || 'Failed to update student allocation');
        }
      } else {
        const res = await vehicleService.allocateVehicle(payload);
        if (res && res.success) {
          showNotification('success', 'Success', 'Student transport allocation completed');
          setShowAssignmentModal(false);
          loadAssignments();
        } else {
          showNotification('error', 'Error', res?.error || 'Allocation failed');
        }
      }
    } catch (err) {
      console.error(err);
      showNotification('error', 'Error', err.message || 'Failed to save student allocation');
    } finally {
      setLoading(false);
    }
  };

  const handleDeallocate = async (id) => {
    if (!window.confirm('Are you sure you want to deallocate this student from transport?')) return;
    try {
      setLoading(true);
      await vehicleService.deallocateVehicle(id);
      showNotification('success', 'Success', 'Student deallocated from transport');
      loadAssignments();
    } catch (err) {
      console.error(err);
      showNotification('error', 'Error', 'Failed to deallocate student');
    } finally {
      setLoading(false);
    }
  };

  // Filtered Students for Assignment workspace
  const activeAllocations = vehicleAssignments.filter(a => a.allocationStatus === 'ALLOCATED');

  const filteredAllocations = activeAllocations.filter(a => {
    const matchClass = !filterClass || a.className === filterClass;
    const matchSection = !filterSection || a.section === filterSection;
    const search = filterSearch.toLowerCase();
    const matchSearch = !filterSearch || 
      (a.studentName || '').toLowerCase().includes(search) || 
      (a.studentId || '').toLowerCase().includes(search) ||
      (a.routeName || '').toLowerCase().includes(search) ||
      (a.vehicleNumber || '').toLowerCase().includes(search);
    return matchClass && matchSection && matchSearch;
  });

  const totalSeats = vehicles.reduce((sum, v) => sum + (v.capacity || 0), 0);
  const activeVehiclesCount = vehicles.filter(v => v.active !== false).length;
  const maintenanceCount = vehicles.filter(v => (v.status || '').toUpperCase() === 'MAINTENANCE').length;

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: '20px', fontFamily: '"Inter", sans-serif' }}>
      
      {/* ── Page Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px', background: '#ffffff', padding: '16px 24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
            <Bus size={22} color="#2563eb" /> Transport Management System
          </h1>
          <p style={{ color: '#64748b', fontSize: '13px', margin: '4px 0 0 32px' }}>
            Manage vehicles, routes, drivers, and student transport allocations for {orgData?.orgName || orgData?.institutionName || orgData?.name || 'your institution'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => {
              setEditingVehicleId(null);
              setVehicleForm({ registrationNumber: '', vehicleType: 'BUS', capacity: 40, driverName: '', driverPhone: '', route: '', status: 'ACTIVE' });
              setShowVehicleModal(true);
            }} 
            style={{ padding: '9px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Plus size={16} /> Add Vehicle
          </button>
          
          <button 
            onClick={() => {
              setAssignmentForm({ studentId: '', vehicleId: '', routeName: '', pickupPoint: '', dropPoint: '', startDate: new Date().toISOString().split('T')[0] });
              setShowAssignmentModal(true);
            }} 
            style={{ padding: '9px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Users size={16} /> Assign Student
          </button>
        </div>
      </div>

      {/* ── 5 Workspaces Navigation Tabs ── */}
      <div style={{ display: 'flex', gap: '8px', background: '#ffffff', padding: '6px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e2e8f0', overflowX: 'auto' }}>
        {[
          { id: 'dashboard', label: 'Dashboard', icon: Bus },
          { id: 'vehicles', label: `Vehicles (${vehicles.length})`, icon: Bus },
          { id: 'routes', label: `Routes & Stops (${vehicleRoutes.length})`, icon: MapPin },
          { id: 'assignments', label: `Student Assignments (${activeAllocations.length})`, icon: Users },
          { id: 'reports', label: 'Reports', icon: Printer }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, padding: '10px 16px', borderRadius: '8px', border: 'none',
                background: isActive ? '#eff6ff' : 'transparent',
                color: isActive ? '#2563eb' : '#475569',
                fontWeight: isActive ? '700' : '500', fontSize: '13px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'all 0.2s', whiteSpace: 'nowrap'
              }}
            >
              <Icon size={16} color={isActive ? '#2563eb' : '#64748b'} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: DASHBOARD WORKSPACE */}
      {/* ========================================================================= */}
      {activeTab === 'dashboard' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Bus size={24} color="#2563eb" />
                </div>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Total Vehicles</span>
                  <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{vehicles.length}</h3>
                </div>
              </div>
            </div>

            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <MapPin size={24} color="#16a34a" />
                </div>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Active Routes</span>
                  <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{vehicleRoutes.length}</h3>
                </div>
              </div>
            </div>

            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#faf5ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Users size={24} color="#9333ea" />
                </div>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Students Assigned</span>
                  <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{activeAllocations.length}</h3>
                </div>
              </div>
            </div>

            <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Wrench size={24} color="#d97706" />
                </div>
                <div>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Under Maintenance</span>
                  <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '2px 0 0 0' }}>{maintenanceCount}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Seat Capacity Progress */}
          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Fleet Transport Capacity Utilization</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#2563eb' }}>
                {activeAllocations.length} / {totalSeats} seats assigned ({totalSeats > 0 ? Math.round((activeAllocations.length / totalSeats) * 100) : 0}%)
              </span>
            </div>
            <div style={{ width: '100%', height: '10px', background: '#e2e8f0', borderRadius: '5px', overflow: 'hidden' }}>
              <div style={{ 
                width: `${totalSeats > 0 ? Math.min(100, (activeAllocations.length / totalSeats) * 100) : 0}%`, 
                height: '100%', 
                background: 'linear-gradient(90deg, #2563eb 0%, #059669 100%)',
                borderRadius: '5px' 
              }} />
            </div>
          </div>

          {/* Operational Summary Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>
            
            {/* Active Fleet Overview */}
            <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 14px 0' }}>Active Fleet Overview</h3>
              {vehicles.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>No vehicles configured yet</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {vehicles.map(v => (
                    <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Bus size={20} color="#2563eb" />
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{v.registrationNumber}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>Route: {v.route || 'Unassigned'} • Driver: {v.driverName || 'N/A'}</div>
                        </div>
                      </div>
                      <span style={{ 
                        fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px',
                        background: (v.status || '').toUpperCase() === 'MAINTENANCE' ? '#fef3c7' : '#dcfce7',
                        color: (v.status || '').toUpperCase() === 'MAINTENANCE' ? '#92400e' : '#166534'
                      }}>
                        {v.status || 'ACTIVE'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick System Alerts */}
            <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 14px 0' }}>Operational Readiness Alerts</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {vehicles.filter(v => !v.driverName).map(v => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '12px', color: '#92400e' }}>
                    <AlertTriangle size={16} color="#d97706" />
                    <span>Vehicle <strong>{v.registrationNumber}</strong> has no driver assigned.</span>
                  </div>
                ))}
                {vehicles.filter(v => !v.route).map(v => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '12px', color: '#1e40af' }}>
                    <AlertTriangle size={16} color="#2563eb" />
                    <span>Vehicle <strong>{v.registrationNumber}</strong> is not mapped to any route.</span>
                  </div>
                ))}
                {vehicles.filter(v => v.driverName && v.route).length === vehicles.length && vehicles.length > 0 && (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#166534', background: '#f0fdf4', borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}>
                    <CheckCircle size={20} color="#16a34a" style={{ marginBottom: '6px' }} />
                    <br/>All vehicles are fully assigned and operational!
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: VEHICLES WORKSPACE */}
      {/* ========================================================================= */}
      {activeTab === 'vehicles' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Registered Fleet Vehicles</h3>
            <button 
              onClick={() => {
                setEditingVehicleId(null);
                setVehicleForm({ registrationNumber: '', vehicleType: 'BUS', capacity: 40, driverName: '', driverPhone: '', driverPassword: '', route: '', alertDistanceKm: 3.0, status: 'ACTIVE' });
                setShowVehicleModal(true);
              }}
              style={{ padding: '8px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={14} /> Add Vehicle
            </button>
          </div>

          {vehicles.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              <Bus size={36} color="#94a3b8" style={{ marginBottom: '10px' }} />
              <p style={{ margin: 0, fontWeight: '600' }}>No vehicles registered yet.</p>
              <button 
                onClick={() => setShowVehicleModal(true)} 
                style={{ marginTop: '12px', background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
              >
                + Register First Vehicle
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={{ padding: '12px' }}>Registration No</th>
                    <th style={{ padding: '12px' }}>Type</th>
                    <th style={{ padding: '12px' }}>Capacity</th>
                    <th style={{ padding: '12px' }}>Assigned Driver</th>
                    <th style={{ padding: '12px' }}>Driver Phone</th>
                    <th style={{ padding: '12px' }}>Mapped Route</th>
                    <th style={{ padding: '12px' }}>🚨 Siren Alert Distance</th>
                    <th style={{ padding: '12px' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map(v => (
                    <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px', fontWeight: '700', color: '#0f172a' }}>{v.registrationNumber}</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{v.vehicleType || 'BUS'}</td>
                      <td style={{ padding: '12px', color: '#475569', fontWeight: '600' }}>{v.capacity} Seats</td>
                      <td style={{ padding: '12px', color: '#0f172a', fontWeight: '600' }}>{v.driverName || '—'}</td>
                      <td style={{ padding: '12px', color: '#2563eb' }}>{v.driverPhone ? `📞 ${v.driverPhone}` : '—'}</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{v.route || 'Unassigned'}</td>
                      <td style={{ padding: '12px', color: '#dc2626', fontWeight: '700' }}>🚨 {v.alertDistanceKm || 3.0} KM</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ 
                          fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px',
                          background: (v.status || '').toUpperCase() === 'MAINTENANCE' ? '#fef3c7' : '#dcfce7',
                          color: (v.status || '').toUpperCase() === 'MAINTENANCE' ? '#92400e' : '#166534'
                        }}>
                          {v.status || 'ACTIVE'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <button 
                          onClick={() => {
                            setEditingVehicleId(v.id);
                            setVehicleForm({
                              registrationNumber: v.registrationNumber,
                              vehicleType: v.vehicleType || 'BUS',
                              capacity: v.capacity || 40,
                              driverName: v.driverName || '',
                              driverPhone: v.driverPhone || '',
                              route: v.route || '',
                              alertDistanceKm: v.alertDistanceKm || 3.0,
                              status: v.status || 'ACTIVE'
                            });
                            setShowVehicleModal(true);
                          }}
                          style={{ background: '#f1f5f9', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#334155' }}
                        >
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: ROUTES & STOPS WORKSPACE */}
      {/* ========================================================================= */}
      {activeTab === 'routes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Transportation Routes</h3>
              <button 
                onClick={() => {
                  setRouteForm({ routeName: '', startPoint: '', endPoint: '', distance: '', estimatedTime: '', vehicleId: '' });
                  setShowRouteModal(true);
                }}
                style={{ padding: '8px 14px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Plus size={14} /> Add Route
              </button>
            </div>

            {vehicleRoutes.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                <MapPin size={36} color="#94a3b8" style={{ marginBottom: '10px' }} />
                <p style={{ margin: 0, fontWeight: '600' }}>No routes created yet.</p>
                <button 
                  onClick={() => setShowRouteModal(true)} 
                  style={{ marginTop: '12px', background: '#0d9488', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                >
                  + Create Route
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
                {vehicleRoutes.map((r, idx) => {
                  const assignedCount = activeAllocations.filter(a => a.routeName === r.routeName || a.vehicleId === r.vehicleId).length;
                  return (
                    <div key={idx} style={{ background: '#f8fafc', borderRadius: '10px', border: '1px solid #cbd5e1', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: '800', color: '#0f172a' }}>{r.routeName}</span>
                        <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', background: '#e0f2fe', color: '#0369a1', borderRadius: '12px' }}>
                          {assignedCount} / {r.capacity} Students
                        </span>
                      </div>

                      <div style={{ fontSize: '12px', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <MapPin size={14} color="#0d9488" />
                        <span><strong>Path:</strong> {r.startPoint} ➔ {r.endPoint}</span>
                      </div>

                      <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', justifyContent: 'space-between' }}>
                        <span>Bus: <strong>{r.vehicleReg || 'Unassigned'}</strong></span>
                        <span>Driver: <strong>{r.driverName || 'N/A'}</strong></span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 4: STUDENT ASSIGNMENTS WORKSPACE */}
      {/* ========================================================================= */}
      {activeTab === 'assignments' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
          
          {/* Filters Bar */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ flex: '1 1 180px' }}>
              <input
                type="text"
                placeholder="Search student or route..."
                value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
              />
            </div>

            <div style={{ width: '140px' }}>
              <select
                value={filterClass}
                onChange={e => setFilterClass(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
              >
                <option value="">All Classes</option>
                {Array.from(new Set(allStudents.map(s => s.className).filter(Boolean))).map(cls => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>

            <button 
              onClick={() => {
                setEditingAssignmentId(null);
                setAssignmentForm({ studentId: '', vehicleId: '', routeName: '', pickupPoint: '', dropPoint: '', alertDistanceKm: 3.0, startDate: new Date().toISOString().split('T')[0] });
                setShowAssignmentModal(true);
              }}
              style={{ padding: '8px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginLeft: 'auto' }}
            >
              + Assign Student to Transport
            </button>
          </div>

          {/* Assignments Table */}
          {filteredAllocations.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
              <Users size={36} color="#94a3b8" style={{ marginBottom: '10px' }} />
              <p style={{ margin: 0, fontWeight: '600' }}>No transport allocations found.</p>
              <button 
                onClick={() => setShowAssignmentModal(true)} 
                style={{ marginTop: '12px', background: '#059669', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
              >
                + Assign Student Now
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                    <th style={{ padding: '12px' }}>Student Name</th>
                    <th style={{ padding: '12px' }}>Roll / Student ID</th>
                    <th style={{ padding: '12px' }}>Class</th>
                    <th style={{ padding: '12px' }}>Assigned Vehicle</th>
                    <th style={{ padding: '12px' }}>Route Name</th>
                    <th style={{ padding: '12px' }}>Pickup Stop</th>
                    <th style={{ padding: '12px' }}>🚨 Siren Alert Distance</th>
                    <th style={{ padding: '12px' }}>Effective Date</th>
                    <th style={{ padding: '12px' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAllocations.map(a => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px', fontWeight: '700', color: '#0f172a' }}>{a.studentName || 'Student'}</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{a.usn || a.studentId}</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{a.className ? `${a.className}${a.section ? ' - ' + a.section : ''}` : '—'}</td>
                      <td style={{ padding: '12px', color: '#2563eb', fontWeight: '700' }}>{a.vehicleNumber || `Bus #${a.vehicleId}`}</td>
                      <td style={{ padding: '12px', color: '#0f172a', fontWeight: '600' }}>{a.routeName || '—'}</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{a.pickupPoint || '—'}</td>
                      <td style={{ padding: '12px', color: '#dc2626', fontWeight: '700' }}>🚨 {a.alertDistanceKm || 3.0} KM</td>
                      <td style={{ padding: '12px', color: '#475569' }}>{formatDate(a.startDate)}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '12px', background: '#dcfce7', color: '#166534' }}>
                          ALLOCATED
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <button 
                          onClick={() => {
                            setEditingAssignmentId(a.id);
                            setAssignmentForm({
                              studentId: a.studentId,
                              vehicleId: a.vehicleId,
                              routeName: a.routeName || '',
                              pickupPoint: a.pickupPoint || '',
                              dropPoint: a.dropPoint || '',
                              alertDistanceKm: a.alertDistanceKm || 3.0,
                              startDate: a.startDate || new Date().toISOString().split('T')[0]
                            });
                            setShowAssignmentModal(true);
                          }}
                          style={{ background: '#eff6ff', border: '1px solid #93c5fd', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#1d4ed8', marginRight: '6px' }}
                        >
                          ✏️ Edit Alert
                        </button>
                        <button 
                          onClick={() => handleDeallocate(a.id)}
                          style={{ background: '#fef2f2', border: '1px solid #fca5a5', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#dc2626' }}
                        >
                          Deallocate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 5: REPORTS WORKSPACE */}
      {/* ========================================================================= */}
      {activeTab === 'reports' && (
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Transport Operational Reports</h3>
            <button onClick={() => window.print()} style={{ padding: '8px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Printer size={14} /> Print Report
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {/* Route Summary */}
            <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px', background: '#f8fafc' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Route-wise Occupancy</h4>
              {vehicleRoutes.map((r, i) => {
                const count = activeAllocations.filter(a => a.routeName === r.routeName || a.vehicleId === r.vehicleId).length;
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e2e8f0', fontSize: '12px' }}>
                    <span style={{ fontWeight: '600', color: '#334155' }}>{r.routeName}</span>
                    <span style={{ fontWeight: '700', color: '#2563eb' }}>{count} Students</span>
                  </div>
                );
              })}
            </div>

            {/* Vehicle Fleet Status */}
            <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px', background: '#f8fafc' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>Vehicle Fleet Breakdown</h4>
              <div style={{ fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Active Registered Vehicles:</span>
                  <strong>{activeVehiclesCount}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Vehicles in Maintenance:</span>
                  <strong>{maintenanceCount}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Fleet Capacity:</span>
                  <strong>{totalSeats} Seats</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL 1: ADD / EDIT VEHICLE ── */}
      {showVehicleModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', width: '100%', maxWidth: '500px', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
              {editingVehicleId ? 'Edit Vehicle' : 'Add New Vehicle'}
            </h3>
            
            <form onSubmit={handleSaveVehicle} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Registration Number *</label>
                <input 
                  type="text" 
                  required 
                  placeholder="e.g. KA-36-AB-1234"
                  value={vehicleForm.registrationNumber}
                  onChange={e => setVehicleForm({ ...vehicleForm, registrationNumber: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Type</label>
                  <select 
                    value={vehicleForm.vehicleType}
                    onChange={e => setVehicleForm({ ...vehicleForm, vehicleType: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  >
                    <option value="BUS">School Bus</option>
                    <option value="VAN">Van</option>
                    <option value="CAR">Car</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Seating Capacity *</label>
                  <input 
                    type="number" 
                    required
                    value={vehicleForm.capacity}
                    onChange={e => setVehicleForm({ ...vehicleForm, capacity: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Driver Name</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Ramesh Kumar"
                    value={vehicleForm.driverName}
                    onChange={e => setVehicleForm({ ...vehicleForm, driverName: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Driver Phone (Login)</label>
                  <input 
                    type="text" 
                    placeholder="e.g. 9876543210"
                    value={vehicleForm.driverPhone}
                    onChange={e => setVehicleForm({ ...vehicleForm, driverPhone: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>🔑 Driver Password</label>
                  <input 
                    type="text" 
                    placeholder="Set Driver Password"
                    value={vehicleForm.driverPassword || ''}
                    onChange={e => setVehicleForm({ ...vehicleForm, driverPassword: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Assigned Route Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. Route 01 - Central Area"
                  value={vehicleForm.route}
                  onChange={e => setVehicleForm({ ...vehicleForm, route: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>🚨 Proximity Siren Alert Distance (KM) *</label>
                <input 
                  type="number" 
                  step="0.5"
                  min="0.5"
                  max="20"
                  required 
                  placeholder="e.g. 3.0"
                  value={vehicleForm.alertDistanceKm}
                  onChange={e => setVehicleForm({ ...vehicleForm, alertDistanceKm: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                />
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                  Parent app will trigger high-priority continuous siren alarm when bus is within this distance (e.g. 3 to 4 KM).
                </span>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button type="button" onClick={() => setShowVehicleModal(false)} style={{ padding: '8px 16px', background: '#f1f5f9', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={loading} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                  {loading ? 'Saving...' : 'Save Vehicle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL 2: ASSIGN STUDENT TO TRANSPORT ── */}
      {showAssignmentModal && (() => {
        const filteredModalStudents = allStudents.filter(s => {
          if (!modalStudentSearch.trim()) return true;
          const q = modalStudentSearch.toLowerCase().trim();
          const stName = (s.name || s.studentName || '').toLowerCase();
          const stRoll = (s.rollNo || s.studentId || s.id || '').toLowerCase();
          const stClass = (s.className || s.class || '').toLowerCase();
          const stSec = (s.section || '').toLowerCase();
          return stName.includes(q) || stRoll.includes(q) || stClass.includes(q) || `${stClass} ${stSec}`.includes(q);
        });

        const selectedStudentObj = allStudents.find(s => String(s.studentId || s.id || s.rollNo) === String(assignmentForm.studentId));

        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
            <div style={{ background: '#ffffff', borderRadius: '12px', width: '100%', maxWidth: '520px', padding: '24px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                  {editingAssignmentId ? 'Edit Student Transport & Siren Alert Settings' : 'Assign Student to Transport'}
                </h3>
                <button 
                  type="button"
                  onClick={() => {
                    setShowAssignmentModal(false);
                    setModalStudentSearch('');
                  }} 
                  style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleSaveAssignment} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>Select Student *</label>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                      {filteredModalStudents.length} of {allStudents.length} students
                    </span>
                  </div>

                  {/* 🔍 Search Input for Students */}
                  <div style={{ position: 'relative', marginBottom: '8px' }}>
                    <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input 
                      type="text" 
                      placeholder="Type student name, ID, or class (e.g. Gururaja, 0030, LKG)..."
                      value={modalStudentSearch}
                      onChange={e => setModalStudentSearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px 8px 32px',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '13px',
                        backgroundColor: '#f8fafc',
                        outline: 'none'
                      }}
                    />
                    {modalStudentSearch && (
                      <button 
                        type="button" 
                        onClick={() => setModalStudentSearch('')} 
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '12px', padding: 0 }}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <select 
                    required
                    value={assignmentForm.studentId}
                    onChange={e => setAssignmentForm({ ...assignmentForm, studentId: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', backgroundColor: '#fff' }}
                  >
                    <option value="">-- Choose Student ({filteredModalStudents.length} available) --</option>
                    {filteredModalStudents.map((s, idx) => {
                      const val = s.studentId || s.id || s.rollNo;
                      const stName = s.name || s.studentName || 'Student';
                      const stRoll = s.rollNo || s.studentId || s.id || '';
                      const stClass = s.className || s.class || '';
                      const stSec = s.section || '';
                      return (
                        <option key={val || idx} value={val}>
                          {stName} {stRoll ? `(${stRoll})` : ''} {stClass ? `- Class ${stClass}${stSec ? ' ' + stSec : ''}` : ''}
                        </option>
                      );
                    })}
                  </select>

                  {filteredModalStudents.length === 0 && (
                    <div style={{ fontSize: '11.5px', color: '#dc2626', marginTop: '4px' }}>
                      No students match "{modalStudentSearch}". Clear search to view all.
                    </div>
                  )}

                  {selectedStudentObj && (
                    <div style={{ marginTop: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', color: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>👤 <strong>{selectedStudentObj.name || selectedStudentObj.studentName}</strong> (ID: {selectedStudentObj.studentId || selectedStudentObj.rollNo}) • Class {selectedStudentObj.className || selectedStudentObj.class || '—'} {selectedStudentObj.section || ''}</span>
                      <span style={{ color: '#166534', fontWeight: '600', fontSize: '11px' }}>✓ Selected</span>
                    </div>
                  )}
                </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Select Vehicle *</label>
                <select 
                  required
                  value={assignmentForm.vehicleId}
                  onChange={e => {
                    const selectedV = vehicles.find(v => String(v.id) === String(e.target.value));
                    setAssignmentForm({ 
                      ...assignmentForm, 
                      vehicleId: e.target.value,
                      routeName: selectedV ? selectedV.route : assignmentForm.routeName 
                    });
                  }}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                >
                  <option value="">-- Choose Vehicle --</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.registrationNumber} ({v.vehicleType}) - Route: {v.route || 'Unassigned'} ({v.capacity} Seats)
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Pickup Point / Stop</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Bus Stand Circle"
                    value={assignmentForm.pickupPoint}
                    onChange={e => setAssignmentForm({ ...assignmentForm, pickupPoint: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Drop Point / Stop</label>
                  <input 
                    type="text" 
                    placeholder="e.g. School Gate"
                    value={assignmentForm.dropPoint}
                    onChange={e => setAssignmentForm({ ...assignmentForm, dropPoint: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>🚨 Student Proximity Siren Alert Distance (KM) *</label>
                <input 
                  type="number" 
                  step="0.5"
                  min="0.5"
                  max="20"
                  required
                  placeholder="e.g. 3.0"
                  value={assignmentForm.alertDistanceKm}
                  onChange={e => setAssignmentForm({ ...assignmentForm, alertDistanceKm: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                />
                <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                  Admin can set custom alert distance for every student individually (defaults to 3.0 KM).
                </span>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Effective Start Date</label>
                <input 
                  type="date" 
                  value={assignmentForm.startDate}
                  onChange={e => setAssignmentForm({ ...assignmentForm, startDate: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button type="button" onClick={() => { setShowAssignmentModal(false); setEditingAssignmentId(null); }} style={{ padding: '8px 16px', background: '#f1f5f9', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={loading} style={{ padding: '8px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                  {loading ? 'Saving...' : editingAssignmentId ? 'Update Settings' : 'Confirm Allocation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    })()}

      {/* ── MODAL 3: ADD ROUTE ── */}
      {showRouteModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#ffffff', borderRadius: '12px', width: '100%', maxWidth: '480px', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Add Transportation Route</h3>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (routeForm.vehicleId) {
                const targetV = vehicles.find(v => String(v.id) === String(routeForm.vehicleId));
                if (targetV) {
                  await vehicleService.updateVehicle(targetV.id, {
                    ...targetV,
                    route: routeForm.routeName,
                    pickupPoints: `${routeForm.startPoint}, ${routeForm.endPoint}`
                  });
                  showNotification('success', 'Success', 'Route mapped to vehicle successfully');
                  setShowRouteModal(false);
                  loadVehicles();
                }
              } else {
                showNotification('info', 'Info', 'Route added to route list');
                setShowRouteModal(false);
              }
            }} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Route Name *</label>
                <input 
                  type="text" 
                  required 
                  placeholder="e.g. Route 01 - Central Area"
                  value={routeForm.routeName}
                  onChange={e => setRouteForm({ ...routeForm, routeName: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Start Point</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Bus Stand"
                    value={routeForm.startPoint}
                    onChange={e => setRouteForm({ ...routeForm, startPoint: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>End Point</label>
                  <input 
                    type="text" 
                    placeholder="e.g. School Campus"
                    value={routeForm.endPoint}
                    onChange={e => setRouteForm({ ...routeForm, endPoint: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>Assign Vehicle to Route</label>
                <select 
                  value={routeForm.vehicleId}
                  onChange={e => setRouteForm({ ...routeForm, vehicleId: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px' }}
                >
                  <option value="">-- Assign Later --</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.registrationNumber} ({v.vehicleType})</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button type="button" onClick={() => setShowRouteModal(false)} style={{ padding: '8px 16px', background: '#f1f5f9', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '8px 16px', background: '#0d9488', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                  Save Route
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default VehicleManagement;
